import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BusinessService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type {
  CreateBusinessRequest,
  UpdateBusinessRequest,
} from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';
import { updateQueryItem, upsertQueryItem } from '@billx/utils';

import { useCurrentUser } from '../users/useCurrentUser';

export const BASE_BUSINESSES_QUERY_KEY = 'accessibleBusinesses';

export const useAccessibleBusinesses = () => {
  const queryClient = useQueryClient();
  const { currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();

  const QUERY_KEY = [BASE_BUSINESSES_QUERY_KEY, currentUser?.id];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await BusinessService.getAccessibleBusinesses();
      return handleNucleusResponse(response).data;
    },
    enabled: !!currentUser,
  });

  const createBusiness = useMutation({
    mutationFn: (data: CreateBusinessRequest) =>
      BusinessService.createBusiness(data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, upsertQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const updateBusiness = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBusinessRequest }) =>
      BusinessService.updateBusiness(id, data),
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, updateQueryItem(response.data));
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: accessibleBusinesses = [], isLoading, ...restQuery } = query;

  return {
    // Query data
    accessibleBusinesses,
    isLoading: isLoading || isCurrentUserLoading,
    ...restQuery,
    // Mutations
    createBusiness,
    updateBusiness,
  };
};
